import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MenuService } from '../../core/menu/menu.service';
import { SettingsService } from '../../core/settings/settings.service';
import { UserblockService } from '../sidebar/userblock/userblock.service';
import { ITenant } from '@shared/services/api/legacy/users-service/user-model';
import { Store } from '@ngrx/store';
import { IState } from '@state/state.module';
import { filter, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, forkJoin as observableForkJoin, combineLatest as observableCombineLatest, Observable, Subject } from 'rxjs';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/legacy/response';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { InvitedeskApiService } from '@shared/services/api/legacy/invitedesk-api.service';
import { AttendeeNotesFilterModel, IAttendeeNote, IAttendeeNotes } from '@shared/services/api/legacy/attendeenotes-service/attendeenotes-model';
import { ModalButtonSet } from '@shared/components/modal-form-host/modal-buttons';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ModalActionStatus } from '@shared/components/modal-form-host/modal-action-status';
import { ModalFormHostStateService } from '@shared/components/modal-form-host/modal-form-host-state.service';
import { IAppState } from '@state/reducers/app/app.reducer';
import { AnimatedButtonState } from '@shared/components/animated-button/animated-button-state';
import { ErrorInfo, IErrorInfo } from '@shared/components/modal-form-host/error-info';
import { NoteType } from '@shared/enums/NoteType';
import { AuthService } from '@shared/services/auth/auth.service';
import { StyleService } from '@shared/services/style/style.service';
import { INotification, NotificationsFilterModel, NotificationsPage, NotificationsPagedData } from '@shared/services/api/dashboard/notifications-service/notifications-model';
import { NotificationType } from '@shared/enums/NotificationType';
import { DashboardApiService } from '@shared/services/api/dashboard/dashboard-api.service';
declare let $: any;

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements AfterViewInit, OnInit, OnDestroy {
    public tenants: ITenant[] = [];
    public defaultTenantId: string;
    public isSupport: boolean;
    private userId: string;
    private userName: string;

    public loading = false;
    public showUserAccount = false;

    public notifications: INotification[] = [];
    public attendeesNotes: IAttendeeNote[] = [];

    public buttonSets = ModalButtonSet;
    public error: IErrorInfo = null;
    public supportingTenant: ITenant;
    private state$: Observable<IAppState>;
    private destroy$ = new Subject();
    public isNavSearchVisible: boolean;
    public dropdownItems = [];
    public productItems = [];

    @ViewChild('fsbutton') public fsbutton;  // the fullscreen button
    @ViewChild('navDropdown') userDropdown!: ElementRef;
    @ViewChild('products') products!: ElementRef;

    @Input()
    get status(): ModalActionStatus { return this.status$.value; }
    set status(value: ModalActionStatus) { this.status$.next(value); }
    public status$: BehaviorSubject<ModalActionStatus> = new BehaviorSubject<ModalActionStatus>(ModalActionStatus.Iddle);

    public modal$: BehaviorSubject<BsModalRef> = new BehaviorSubject<BsModalRef>(undefined);
    get modal(): BsModalRef { return this.modal$.value; }
    set modal(value: BsModalRef) { this.modal$.next(value); }

    constructor(
        public menu: MenuService,
        public userblockService: UserblockService,
        public settings: SettingsService,
        private store: Store<IState>,
        private oidcSecurityService: OidcSecurityService,
        private invitedeskApi: InvitedeskApiService,
        private dashboardApi: DashboardApiService,
        private modalService: BsModalService,
        private modelStateService: ModalFormHostStateService,
        private authService: AuthService,
        public styleService: StyleService,
        private cd: ChangeDetectorRef
    ) { }

    public ngOnInit() {
        observableCombineLatest(this.status$, this.modal$, this.modelStateService.animatedButtonState$).pipe(
            takeUntil(this.destroy$),
            filter(([action, modal, animated]) => !!action && !!modal && action === ModalActionStatus.Finished && !!animated && animated === AnimatedButtonState.Default),)
            .subscribe(([action, modal]) => modal.hide());

        this.isNavSearchVisible = false;

        this.store.select(x => x.app.profile)
        .pipe(
            takeUntil(this.destroy$),
            filter(x => !x.pending)
        )
        .subscribe(profile => {
            this.userId = profile.id;
            this.userName = `${profile.firstName} ${profile.name}`;
            if (profile.isSupport) {
                this.isSupport = true;
                this.supportingTenant = profile.isSupport ? profile.tenants[0] : null;
            } else {
                this.tenants = profile.tenants;
                const token = this.authService.retrieveToken();
                this.defaultTenantId = token?.decoded?.ten;
            }
        });
    }

    ngAfterViewInit(): void {
        this.dropdownItems = this.userDropdown ? [this.userDropdown] : [];
        this.productItems = this.products ? [this.products] : [];
        this.cd.detectChanges();
    }

    public toggleUserBlock(event) {
        event.preventDefault();
        this.userblockService.toggleVisibility();
    }

    public openNavSearch(event) {
        event.preventDefault();
        event.stopPropagation();
        this.setNavSearchVisible(true);
    }

    public setNavSearchVisible(stat: boolean) {
        this.isNavSearchVisible = stat;
    }

    public getNavSearchVisible() {
        return this.isNavSearchVisible;
    }

    public logout() {
        if (this.isSupport) {
            window.close();
        } else {
            this.oidcSecurityService.logoff('dashboard-ui');
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    public changeTenant(selectedTenantId: string) {
        if (selectedTenantId === this.defaultTenantId) {
            return;
        }

        this.authService.requestNewAccessToken(selectedTenantId);
    }

    public openNotifications(template: TemplateRef<any>) {
        this.loading = true;
        const page: NotificationsPage = new NotificationsPage();
        this.resetPageNotifications(page);
        observableForkJoin(
            this.dashboardApi.notifications.getNotifications(page)
        ).subscribe(
        (
            response: [
                SuccessResponse<NotificationsPagedData<INotification>>
            ]
        ) => {
                this.notifications = response[0].data.notificationLogs;
                this.modal = this.modalService.show(template, { class: 'modal-xl-70', backdrop: 'static', keyboard: false });
                this.loading = false;
            },
            (error: ErrorResponse) => {
                this.loading = false;
                console.error('show notification', error.error);
            }
        );
    }

    public openTasks(template: TemplateRef<any>) {
        this.loading = true;
        const attendeeNotesFilterModel = new AttendeeNotesFilterModel();
        attendeeNotesFilterModel.noteType = NoteType.Task;
        attendeeNotesFilterModel.userId = this.userId;
        observableForkJoin(
            this.invitedeskApi.attendeeNotes.getAttendeesNotes(attendeeNotesFilterModel),
        ).subscribe(
        (
            response: [
                SuccessResponse<IAttendeeNotes>
            ]
        ) => {
                this.attendeesNotes = response[0].data?.attendeeNotes;
                this.modal = this.modalService.show(template, { class: 'modal-xl-70', backdrop: 'static', keyboard: false });
                this.loading = false;
            },
            (error: ErrorResponse) => {
                this.loading = false;
                console.error('show notification', error.error);
            }
        );
    }

    public submitNotesAttendees(ids: string[]) {
        this.status = ModalActionStatus.Pending;
        this.invitedeskApi.attendeeNotes
            .completeAttendeesNotes(ids)
            .subscribe(
                (x: SuccessResponse<boolean>) => {
                    if (!!x.data) {
                        this.status = ModalActionStatus.Finished;
                    } else {
                        this.status = ModalActionStatus.Error;
                        this.error = new ErrorInfo(x?.messages[0]?.text);
                    }
                },
                (error: ErrorResponse) => {
                    this.status = ModalActionStatus.Error;
                    console.error('onAttendeesNotesCompleted', error);
                });
    }

    private resetPageNotifications(page: NotificationsPage) {
        page.pageNumber = 0;
        page.size = 100;
        page.sortProperty = '';
        page.sortDirection = '';
        page.filterModel = new NotificationsFilterModel();

        if (localStorage.getItem('filter-notifications')) {
          page.filterModel = JSON.parse(localStorage.getItem('filter-notifications'));
        } else {
            const notificationsFilterModel = new NotificationsFilterModel();
            notificationsFilterModel.searchValue = '';
            notificationsFilterModel.notificationType = NotificationType.Undefined;
            page.filterModel = notificationsFilterModel;
        }
    }
}

